import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useIsMembershipActive } from "../../../contexts/MembershipContext";
import { api } from "../../../singleton/axios";
import { savedScreensQueryKey, screenNewMatchesPrefix } from "./queryKeys";
import type {
  SaveScreenBody,
  SavedScreen,
  SavedScreensResponse,
  ScreenFilters,
  ScreenSort,
} from "../types";
import {
  SCREENER_READ_TIMEOUT_MS,
  screenerRetry,
  screenerRetryDelay,
} from "./screenerRetry";

export { savedScreensQueryKey };

/** A saved screen that no longer exists, or was never the caller's. */
export const isScreenNotFound = (error: unknown): boolean =>
  (error as { response?: { status?: number } } | null)?.response?.status ===
  404;

const serverMessage = (error: unknown, fallback: string): string => {
  const data = (
    error as { response?: { data?: { message?: string; error?: string } } }
  )?.response?.data;
  return data?.message ?? data?.error ?? fallback;
};

export function useSavedScreens() {
  const { isMembershipActive } = useIsMembershipActive();
  return useQuery<SavedScreensResponse, Error>({
    queryKey: savedScreensQueryKey,
    queryFn: async () => {
      const { data } = await api.get<SavedScreensResponse>(
        "/screener/screens",
        {
          timeout: SCREENER_READ_TIMEOUT_MS,
        },
      );
      return data;
    },
    enabled: isMembershipActive,
    // Alert status changes on the server (a baseline lands, a plan pauses
    // them), so the list refetches whenever it is shown or the app regains
    // focus rather than trusting a copy from minutes ago. It's one light read.
    staleTime: 0,
    refetchOnWindowFocus: true,
    retry: screenerRetry,
    retryDelay: screenerRetryDelay,
  });
}

export function useSavedScreen(id: string | null) {
  const { isMembershipActive } = useIsMembershipActive();
  return useQuery<SavedScreen, Error>({
    queryKey: ["screener", "saved", id],
    queryFn: async () => {
      const { data } = await api.get<SavedScreen>(
        `/screener/screens/${encodeURIComponent(id ?? "")}`,
        {
          timeout: SCREENER_READ_TIMEOUT_MS,
        },
      );
      return data;
    },
    enabled: isMembershipActive && id !== null,
    staleTime: 5 * 60 * 1000,
    retry: screenerRetry,
    retryDelay: screenerRetryDelay,
  });
}

export function useSaveScreen() {
  const queryClient = useQueryClient();
  return useMutation<SavedScreen, Error, SaveScreenBody>({
    mutationFn: async (body) => {
      try {
        const { data } = await api.post<SavedScreen>("/screener/screens", body);
        return data;
      } catch (error) {
        throw new Error(serverMessage(error, "Could not save the screen"));
      }
    },
    // The dialog confirms the save, with the alerts outcome when it asked for them.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: savedScreensQueryKey }),
  });
}

/** "Save changes": the screen's filters and sort, in place. */
export function useUpdateScreenFilters() {
  const queryClient = useQueryClient();
  return useMutation<
    SavedScreen,
    Error,
    {
      id: string;
      filters: ScreenFilters;
      /** null clears a saved order back to the default. */
      sort?: ScreenSort | null;
      presetId?: string;
    }
  >({
    mutationFn: async ({ id, filters, sort, presetId }) => {
      try {
        const { data } = await api.patch<SavedScreen>(
          `/screener/screens/${encodeURIComponent(id)}`,
          { filters, sort, presetId },
        );
        return data;
      } catch (error) {
        throw new Error(serverMessage(error, "Could not save the changes"));
      }
    },
    // The response is the screen as saved, so "Save changes" clears at once.
    onSuccess: (saved) => {
      queryClient.setQueryData(["screener", "saved", saved.id], saved);
      queryClient.setQueryData<SavedScreensResponse>(
        savedScreensQueryKey,
        (current) =>
          current && {
            ...current,
            screens: current.screens.map((screen) =>
              screen.id === saved.id ? { ...screen, ...saved } : screen,
            ),
          },
      );
      void queryClient.invalidateQueries({ queryKey: savedScreensQueryKey });
      // New filters leave the alerts made under the old ones unpinned.
      void queryClient.invalidateQueries({
        queryKey: screenNewMatchesPrefix(saved.id),
      });
    },
  });
}

export function useRenameScreen() {
  const queryClient = useQueryClient();
  return useMutation<SavedScreen, Error, { id: string; name: string }>({
    mutationFn: async ({ id, name }) => {
      try {
        const { data } = await api.patch<SavedScreen>(
          `/screener/screens/${encodeURIComponent(id)}`,
          { name },
        );
        return data;
      } catch (error) {
        throw new Error(serverMessage(error, "Could not rename the screen"));
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: savedScreensQueryKey }),
    onError: (error) =>
      enqueueSnackbar(error.message, {
        variant: "error",
        autoHideDuration: 5000,
      }),
  });
}

export function useDeleteScreen() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (id) => {
      try {
        await api.delete(`/screener/screens/${encodeURIComponent(id)}`);
      } catch (error) {
        throw new Error(serverMessage(error, "Could not delete the screen"));
      }
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: savedScreensQueryKey }),
    onError: (error) =>
      enqueueSnackbar(error.message, {
        variant: "error",
        autoHideDuration: 5000,
      }),
  });
}
